import { Selector } from '@ngxs/store';

import {
  applyGridConfig,
  getNumberOfFilteredRecords,
  isAnyFilterActive,
} from '@customer-portal/shared/helpers/grid';
import {
  FilteringConfig,
  FilterOptions,
  GridConfig,
} from '@customer-portal/shared/models/grid';

import {
  SettingsCompanyDetailsCountryListData,
  SettingsCompanyDetailsData,
} from '../../models';
import { SettingsState, SettingsStateModel } from '../settings.state';

export interface SettingsCompanyDetailsAreaCodeOption {
  label: string;
  value: string;
  mobileCountryCode: string;
}

export class SettingsSelectors {
  @Selector([SettingsSelectors._isUserAdmin])
  static isUserAdmin(isUserAdmin: boolean): boolean {
    return isUserAdmin;
  }

  @Selector([SettingsSelectors._updatePendingParentCompany])
  static updatePendingParentCompany(updatePending: boolean): boolean {
    return updatePending;
  }

  @Selector([SettingsSelectors._legalEntityList])
  static legalEntityList(
    legalEntityList: SettingsCompanyDetailsData[],
  ): SettingsCompanyDetailsData[] {
    return legalEntityList;
  }

  @Selector([SettingsSelectors._totalLegalEntityList])
  static totalLegalEntityList(totalLegalEntityList: number): number {
    return totalLegalEntityList;
  }

  @Selector([SettingsSelectors._parentCompany])
  static parentCompany(
    parentCompany: SettingsCompanyDetailsData | null,
  ): SettingsCompanyDetailsData | null {
    return parentCompany;
  }

  @Selector([SettingsSelectors._legalEntityFilterOptions])
  static legalEntityFilterOptions(
    legalEntityFilterOptions: FilterOptions,
  ): FilterOptions {
    return legalEntityFilterOptions;
  }

  @Selector([SettingsSelectors._legalEntityFilteringConfig])
  static legalEntityFilteringConfig(
    legalEntityFilteringConfig: FilteringConfig,
  ): FilteringConfig {
    return legalEntityFilteringConfig;
  }

  @Selector([SettingsSelectors._hasLegalEntityActiveFilters])
  static hasLegalEntityActiveFilters(
    hasLegalEntityActiveFilters: boolean,
  ): boolean {
    return hasLegalEntityActiveFilters;
  }

  @Selector([SettingsSelectors._legalEntityListCounter])
  static legalEntityListCounter(legalEntityListCounter: number): number {
    return legalEntityListCounter;
  }

  @Selector([SettingsSelectors._countryList])
  static countryList(
    countryList: SettingsCompanyDetailsCountryListData[],
  ): SettingsCompanyDetailsCountryListData[] {
    return countryList;
  }

  @Selector([SettingsSelectors._countryList])
  static areaCodes(
    countryList: SettingsCompanyDetailsCountryListData[],
  ): SettingsCompanyDetailsAreaCodeOption[] {
    const options = countryList
      .filter(
        (country) =>
          country.mobileCountryCode && country.mobileCountryCode.trim(),
      )
      .slice()
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((country) => ({
        label: country.label,
        value: country.countryCode,
        mobileCountryCode: country.mobileCountryCode,
      }));

    return options;
  }

  @Selector([SettingsSelectors.areaCodes])
  static defaultAreaCode(
    areaCodes: SettingsCompanyDetailsAreaCodeOption[],
  ): string | null {
    return areaCodes[0]?.value ?? null;
  }

  @Selector([SettingsSelectors._countryActiveId])
  static countryActiveId(countryActiveId: number | null): number | null {
    return countryActiveId;
  }

  @Selector([SettingsSelectors._isEditCompanyDetailsFormValid])
  static isEditCompanyDetailsFormValid(
    isEditCompanyDetailsFormValid: boolean,
  ): boolean {
    return isEditCompanyDetailsFormValid;
  }

  @Selector([SettingsSelectors._legalEntityGridConfig])
  static legalEntityGridConfig(legalEntityGridConfig: GridConfig): GridConfig {
    return legalEntityGridConfig;
  }

  @Selector([SettingsSelectors._companyDetailsLoading])
  static companyDetailsLoading(isLoading: boolean): boolean {
    return isLoading;
  }

  @Selector([SettingsSelectors._companyDetailsLoaded])
  static companyDetailsLoaded(loaded: boolean): boolean {
    return loaded;
  }

  @Selector([SettingsSelectors._hasCompanyDetailsApiError])
  static hasCompanyDetailsApiError(hasError: boolean): boolean {
    return hasError;
  }

  @Selector([SettingsState])
  private static _legalEntityGridConfig(state: SettingsStateModel): GridConfig {
    return state.legalEntityGridConfig;
  }

  @Selector([SettingsState])
  private static _isUserAdmin(state: SettingsStateModel): boolean {
    return state.isUserAdmin;
  }

  @Selector([SettingsState])
  private static _updatePendingParentCompany(
    state: SettingsStateModel,
  ): boolean {
    return !!state?.parentCompany?.updatePending;
  }

  @Selector([SettingsState])
  private static _legalEntityList(
    state: SettingsStateModel,
  ): SettingsCompanyDetailsData[] {
    const { legalEntityList, legalEntityGridConfig } = state;

    return applyGridConfig(legalEntityList, legalEntityGridConfig);
  }

  @Selector([SettingsState])
  private static _totalLegalEntityList(state: SettingsStateModel): number {
    const { legalEntityList, legalEntityGridConfig } = state;

    return getNumberOfFilteredRecords(legalEntityList, legalEntityGridConfig);
  }

  @Selector([SettingsState])
  private static _parentCompany(
    state: SettingsStateModel,
  ): SettingsCompanyDetailsData | null {
    return state?.parentCompany;
  }

  @Selector([SettingsState])
  private static _legalEntityFilterOptions(
    state: SettingsStateModel,
  ): FilterOptions {
    return state.legalEntityFilterOptions;
  }

  @Selector([SettingsState])
  private static _legalEntityFilteringConfig(
    state: SettingsStateModel,
  ): FilteringConfig {
    return state.legalEntityGridConfig.filtering;
  }

  @Selector([SettingsState])
  private static _hasLegalEntityActiveFilters(
    state: SettingsStateModel,
  ): boolean {
    return isAnyFilterActive(state.legalEntityGridConfig.filtering);
  }

  @Selector([SettingsState])
  private static _legalEntityListCounter(state: SettingsStateModel): number {
    return state?.legalEntityListCounter;
  }

  @Selector([SettingsState])
  private static _countryList(
    state: SettingsStateModel,
  ): SettingsCompanyDetailsCountryListData[] {
    return state?.countryList;
  }

  @Selector([SettingsState])
  private static _countryActiveId(state: SettingsStateModel): number | null {
    return state?.countryActiveId;
  }

  @Selector([SettingsState])
  private static _isEditCompanyDetailsFormValid(
    state: SettingsStateModel,
  ): boolean {
    return state.isEditCompanyDetailsFormValid;
  }

  @Selector([SettingsState])
  private static _companyDetailsLoading(state: SettingsStateModel): boolean {
    return !state.loadedStates.companyDetails;
  }

  @Selector([SettingsState])
  private static _companyDetailsLoaded(state: SettingsStateModel): boolean {
    return state.loadedStates.companyDetails;
  }

  @Selector([SettingsState])
  private static _hasCompanyDetailsApiError(
    state: SettingsStateModel,
  ): boolean {
    const error = state.loadingErrors.companyDetails;

    if (!error || error.trim() === '') {
      return false;
    }

    return true;
  }
}
